import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui';
import { mainNavigation, ctaNavigation } from '../../data/navigation';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'bg-white/80 backdrop-blur-md shadow-glass border-b border-white/20 py-2'
          : 'bg-transparent py-4'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-shrink-0"
          >
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="/images/logo.png"
                alt="Pragyavan Wellness Centre"
                className="w-12 h-12 object-contain transition-transform duration-300 group-hover:scale-110"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-display font-semibold text-pragyavan-brown group-hover:text-pragyavan-gold transition-colors duration-300">
                  Pragyavan
                </h1>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {mainNavigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-300
                  ${isActiveLink(item.path)
                    ? 'text-pragyavan-brown'
                    : 'text-warm-gray hover:text-pragyavan-brown hover:bg-pragyavan-gold/10'
                  }
                `}
              >
                {item.label}
                {isActiveLink(item.path) && (
                  <motion.div
                    layoutId="activeLink"
                    className="absolute inset-0 bg-pragyavan-gold/20 rounded-full -z-10"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Button & Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            <Link to={ctaNavigation.path} className="hidden sm:block">
              <Button
                variant="primary"
                size="medium"
                className="shadow-lg shadow-pragyavan-gold/20 hover:shadow-xl hover:shadow-pragyavan-gold/30"
              >
                {ctaNavigation.label}
              </Button>
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-full text-warm-gray hover:text-pragyavan-brown hover:bg-pragyavan-cream transition-colors duration-200"
              aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-warm-gray/10 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {mainNavigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    block px-4 py-3 text-base font-medium rounded-xl transition-all duration-200
                    ${isActiveLink(item.path)
                      ? 'text-pragyavan-brown bg-pragyavan-gold/20'
                      : 'text-warm-gray hover:text-pragyavan-brown hover:bg-pragyavan-cream/50'
                    }
                  `}
                >
                  {item.label}
                </Link>
              ))}

              {/* Mobile CTA */}
              <div className="pt-4 mt-4 border-t border-warm-gray/10">
                <Link to={ctaNavigation.path}>
                  <Button variant="primary" size="medium" fullWidth className="shadow-md">
                    {ctaNavigation.label}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;